import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AlertCircle, Shield } from 'lucide-react';
import { useAuth } from './AuthContext';
import { authService } from './services/authService';
import { TWO_FA_CODE_LENGTH, TRUST_DEVICE_DAYS } from './config/constants';
import AuthLayout from './components/auth/AuthLayout';
import AuthCard from './components/auth/AuthCard';
import OtpSlotField from './components/auth/OtpSlotField';
import Button from './components/ui/Button';

const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email || 'your email';

  const [localPart, domainPart] = email.split('@');
  const domainSegments = domainPart.split('.');
  const domainName = domainSegments.shift() || '';
  const domainSuffix = domainSegments.join('.');

  const maskSegment = (segment, visiblePrefix = 2) => {
    if (segment.length <= visiblePrefix) return segment;
    return `${segment.slice(0, visiblePrefix)}${'•'.repeat(Math.max(segment.length - visiblePrefix, 2))}`;
  };

  const maskedLocal = maskSegment(localPart, Math.min(2, localPart.length));
  const maskedDomain = maskSegment(domainName, 1);

  return domainSuffix
    ? `${maskedLocal}@${maskedDomain}.${domainSuffix}`
    : `${maskedLocal}@${maskedDomain}`;
};

function Verify2FA() {
  const location = useLocation();
  const navigate = useNavigate();
  const otpInputRef = useRef(null);
  const { login } = useAuth();

  const email = location.state?.email;

  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendState, setResendState] = useState('idle');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [cookiesEnabled, setCookiesEnabled] = useState(true);
  const resendIntervalRef = useRef(null);
  const verificationVersion = useRef(0);
  const resendVersion = useRef(0);

  useEffect(() => {
    if (!email) {
      navigate('/login', { replace: true });
      return;
    }
    setCookiesEnabled(navigator.cookieEnabled !== false);
  }, [email, navigate]);

  useEffect(() => {
    if (!error) return;
    otpInputRef.current?.focus();
  }, [error]);

  useLayoutEffect(() => {
    setCode('');
    setTrustDevice(false);
    setError('');
    setLoading(false);
    setResendState('idle');
    setResendCooldown(0);
    return () => {
      verificationVersion.current += 1;
      resendVersion.current += 1;
      clearInterval(resendIntervalRef.current);
      resendIntervalRef.current = null;
    };
  }, [email, location.key]);

  const maskedEmail = useMemo(() => maskEmail(email), [email]);

  if (!email) {
    return null;
  }

  const goToSignIn = ({ prefilledEmail, clearEmail = false } = {}) => {
    verificationVersion.current += 1;
    resendVersion.current += 1;
    clearInterval(resendIntervalRef.current);
    resendIntervalRef.current = null;
    navigate('/login', {
      replace: true,
      state: clearEmail ? { clearEmail: true } : { prefilledEmail },
    });
  };

  const handleResend = async () => {
    if (!email || resendState === 'sending' || resendCooldown > 0) return;
    const version = ++resendVersion.current;
    const isCurrentRequest = () => version === resendVersion.current;

    setError('');
    setResendState('sending');

    try {
      await authService.resend2FA(email);
      if (!isCurrentRequest()) return;
      setResendState('sent');
      setResendCooldown(60);

      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
      }

      resendIntervalRef.current = setInterval(() => {
        if (!isCurrentRequest()) return;
        setResendCooldown((currentValue) => {
          if (currentValue <= 1) {
            clearInterval(resendIntervalRef.current);
            resendIntervalRef.current = null;
            setResendState('idle');
            return 0;
          }
          return currentValue - 1;
        });
      }, 1000);
    } catch (submissionError) {
      if (!isCurrentRequest()) return;
      setResendState('idle');
      setError(submissionError.message || 'Couldn’t resend the code.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const version = ++verificationVersion.current;
    setLoading(false);
    const isCurrentRequest = () => version === verificationVersion.current;
    setError('');

    if (code.length !== TWO_FA_CODE_LENGTH) {
      setError(`Please enter a ${TWO_FA_CODE_LENGTH}-digit code`);
      return;
    }

    setLoading(true);

    try {
      const data = await authService.verify2FA(email, code, trustDevice);
      if (!isCurrentRequest()) return;

      login(
        { accessToken: data.accessToken, refreshToken: data.refreshToken },
        data.user,
        data.household,
        data.onboarding,
        data.billing || null,
        data.features || {}
      );

      if (!data.onboarding?.completed) {
        const step = data.onboarding?.currentStep || 1;
        navigate(`/onboarding?step=${step}`);
      } else {
        navigate('/dashboard');
      }
    } catch (submissionError) {
      if (!isCurrentRequest()) return;
      setError(submissionError.message || 'An error occurred. Please try again.');
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  };

  const inputMessageId = 'verification-code-message';
  const showCookiesNote = trustDevice && !cookiesEnabled;

  return (
    <AuthLayout>
      <AuthCard>
        <div className="mx-auto w-full max-w-[380px]">
          <div className="auth-heading">
            <div className="auth-status-icon">
              <Shield className="h-8 w-8" strokeWidth={2.2} />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-[var(--color-text-primary)]">
              Enter Sign-In Code
            </h1>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
              We sent a 6-digit code to{' '}
              <span className="text-[var(--color-text-primary)]">{maskedEmail}</span>
            </p>
            <p className="mt-1 text-sm text-[var(--color-text-muted)]">
              Check spam if you can&apos;t find the code in your inbox.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div>
              <OtpSlotField
                ref={otpInputRef}
                value={code}
                onChange={(nextCode) => {
                  setCode(nextCode);
                  if (error) setError('');
                }}
                length={TWO_FA_CODE_LENGTH}
                describedBy={inputMessageId}
                autoFocus
                invalid={Boolean(error)}
              />

              <div
                id={inputMessageId}
                className="mt-3 min-h-5 text-center text-sm"
                aria-live={error ? 'assertive' : 'polite'}
              >
                {error ? (
                  <span
                    role="alert"
                    className="inline-flex items-start gap-2 text-left auth-danger"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </span>
                ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="trustDevice"
                className="flex cursor-pointer items-center gap-3 text-sm text-[var(--color-text-secondary)]"
              >
                <input
                  id="trustDevice"
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(event) => setTrustDevice(event.target.checked)}
                  className="h-4 w-4 rounded border-[var(--color-border-strong)] bg-[var(--color-surface-input)] text-[var(--color-accent)] focus:ring-[var(--color-focus-ring)]"
                />
                <span>Trust this device for {TRUST_DEVICE_DAYS} days</span>
              </label>

              <div className="min-h-4 pl-7 text-xs text-[var(--color-text-secondary)]">
                {showCookiesNote ? (
                  <span>
                    We can&apos;t remember this device if cookies are disabled.
                  </span>
                ) : null}
              </div>
            </div>

            <Button
              type="submit"
              fullWidth
              disabled={code.length !== TWO_FA_CODE_LENGTH}
              loading={loading}
              loadingLabel="Verifying…"
            >
              Verify & Continue
            </Button>

            <div className="flex flex-col items-center gap-2 text-sm sm:flex-row sm:justify-center">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleResend}
                loading={resendState === 'sending'}
                loadingLabel="Resending…"
                disabled={resendCooldown > 0}
                className="h-auto min-h-0 px-2 py-1 font-medium underline-offset-4 hover:underline"
              >
                {resendCooldown > 0 ? `Sent · ${resendCooldown}s` : 'Resend code'}
              </Button>
              <span className="hidden text-[var(--color-text-muted)] sm:inline">•</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => goToSignIn({ prefilledEmail: email })}
                className="h-auto min-h-0 px-2 py-1 font-medium underline-offset-4 hover:underline"
              >
                Back to sign in
              </Button>
            </div>
          </form>
        </div>
      </AuthCard>
    </AuthLayout>
  );
}

export default Verify2FA;
